import { Button, Text } from "north.js";
import React, { useEffect, useRef } from "react";
import Modal from "react-modal";
import styled from "styled-components";

import { errorToast, FastQrReader, Icon } from "src/shared/components";
import { ViewFinder } from "src/static/img";
import { useGooseGamePlayerLazyQuery } from "src/views/goose-games/hackers/graphql/gooseGamePlayer.generated";
import { GGPlayer } from "src/views/goose-games/types";
import { useFindEventAttendeeByBadgeCodeLazyQuery } from "src/views/volunteer/event-redemption/graphql/findEventAttendeeByBadgeCode.generated";
import { useFindEventAttendeeByEmailLazyQuery } from "src/views/volunteer/registration/graphql/findEventAttendeeByEmail.generated";

interface ScanCameraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onManualInput: () => void;
  onScanResult: (result: GGPlayer) => void;
}

const ViewFinderIndicator = styled(ViewFinder)`
  top: 0px;
  left: 0px;
  z-index: 100;
  box-sizing: border-box;
  position: absolute;
  width: 100%;
  height: 100%;
  padding: 180px;
  @media screen and (max-width: 1280px) {
    padding: 150px;
  }
  @media screen and (max-width: 768px) {
    padding: 100px;
  }
  margin-top: -50px;
`;

const ScanCameraModal: React.FC<ScanCameraModalProps> = ({
  isOpen,
  onClose,
  onManualInput,
  onScanResult,
}) => {
  const scanInProgressRef = useRef(false);

  const [findAttendeeByEmail, { data: emailData, error: emailError }] =
    useFindEventAttendeeByEmailLazyQuery();

  const [findAttendeeByBadgeCode, { data: badgeData, error: badgeError }] =
    useFindEventAttendeeByBadgeCodeLazyQuery();

  const [gooseGamePlayer, { data: playerData, error: playerError }] =
    useGooseGamePlayerLazyQuery();

  useEffect(() => {
    if (playerData?.gooseGamePlayer) {
      const ggPlayer = playerData.gooseGamePlayer;

      const player: GGPlayer = {
        event_attendee: {
          user: {
            id: ggPlayer.event_attendee.user.id,
            name: ggPlayer.event_attendee?.user?.name ?? "",
            email: ggPlayer.event_attendee?.user?.email ?? "",
          },
          badge_code: ggPlayer.event_attendee?.badge_code ?? null,
        },
        event_attendee_id: ggPlayer.event_attendee_id,
        current_points: ggPlayer.current_points,
        total_points: ggPlayer.total_points,
        created_at: ggPlayer.created_at,
      };
      onScanResult(player);
      scanInProgressRef.current = false;
      onClose();
    } else if (playerError) {
      errorToast("Error loading player data");
      scanInProgressRef.current = false;
      onClose();
    }
  }, [playerData, playerError, onScanResult, onClose]);

  useEffect(() => {
    const attendee = emailData?.findEventAttendeeByEmail;
    if (attendee) {
      gooseGamePlayer({
        variables: { event_attendee_id: attendee.eventAttendeeId },
      });
    } else if ((emailData && !attendee) || emailError) {
      errorToast("Error finding goose game player.");
      scanInProgressRef.current = false;
      onClose();
    }
  }, [emailData, emailError, gooseGamePlayer, onClose]);

  useEffect(() => {
    const attendee = badgeData?.findEventAttendeeByBadgeCode;
    if (attendee) {
      gooseGamePlayer({
        variables: { event_attendee_id: attendee.eventAttendeeId },
      });
    } else if ((badgeData && !attendee) || badgeError) {
      errorToast("Error finding goose game player.");
      scanInProgressRef.current = false;
      onClose();
    }
  }, [badgeData, badgeError, gooseGamePlayer, onClose]);

  const handleScan = (result: string) => {
    if (scanInProgressRef.current) return;

    scanInProgressRef.current = true;

    if (!result.includes("@")) {
      const badge_code = result.slice(result.lastIndexOf("/") + 1);
      findAttendeeByBadgeCode({ variables: { badge_code } });
    } else {
      try {
        findAttendeeByEmail({ variables: { email: result } });
      } catch (error) {
        scanInProgressRef.current = false;
        errorToast("Error finding goose game player.");
      }
    }
  };

  return (
    <CameraModal
      isOpen={isOpen}
      contentLabel="QR Scanner"
      onRequestClose={onClose}
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 100,
        },
      }}
    >
      <VideoHeader>
        <button onClick={onClose}>
          <Icon name="chevron-left" size="title" color="white" />
        </button>
        <div>
          <Text type="body1" style={{ color: "white" }}>
            Scan Hacker&apos;s QR Code
          </Text>
        </div>
      </VideoHeader>

      <VideoWrapper>
        <FastQrReader
          onResult={async (result) => {
            if (result) {
              handleScan(result.getText());
            }
          }}
          containerStyle={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
          videoContainerStyle={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            padding: "0",
          }}
          videoStyle={{
            width: "100%",
            objectFit: "cover",
            height: "100%",
            position: "relative",
          }}
          ViewFinder={ViewFinderIndicator}
        />
      </VideoWrapper>

      <ManualButton color="primary" size="md" onClick={onManualInput}>
        Manually input code
      </ManualButton>
    </CameraModal>
  );
};

export default ScanCameraModal;

const CameraModal = styled(Modal)`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  background: white;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

const VideoHeader = styled.div`
  width: 100%;
  display: flex;
  background: #1f2937;
  padding: 29px 25px;
  align-items: center;
  div {
    display: flex;
    flex-direction: column;
    padding-left: 12px;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ManualButton = styled(Button)`
  --buttonWidth: 250px;
  position: fixed;
  width: var(--buttonWidth) !important;
  bottom: 78px;
  left: calc(50vw - var(--buttonWidth) / 2);
  z-index: 10000;
  height: 42px !important;
`;
